import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, tokenStore } from '../api/client'

export type SseEvent = {
  id: number
  user_id: number
  project_id: number | null
  kind: string
  payload: Record<string, unknown>
  created_at: string
}

type Options = {
  /** Restrict events to this project_id. */
  projectId?: number
  /** Map event kind → query keys to invalidate when received. */
  invalidate?: Record<string, Array<readonly unknown[]>>
  /** Custom handler, called for every event. */
  onEvent?: (event: SseEvent) => void
  /** Skip mounting the EventSource (e.g. during transitions). */
  enabled?: boolean
}

/**
 * Subscribe to /api/v1/stream (server-sent events).
 *
 * Server uses an 8-second cycle (DreamHost FPM-worker friendly) and EventSource
 * auto-reconnects in 1 s, so effective realtime is unchanged. We use `?token=`
 * because EventSource can't set Authorization headers.
 *
 * Opt-out for offline / explicit-disable: set window.__atlas_disable_sse = true.
 *
 * On each incoming event:
 *   1. Calls `onEvent` if provided.
 *   2. For any matching kind in `invalidate`, calls
 *      `queryClient.invalidateQueries({ queryKey })` for every listed key.
 *
 * Last seen event id is preserved in component-local ref so reconnects pick
 * up from where we left off (the server also honours the `Last-Event-ID`
 * header which EventSource sets automatically).
 */
export function useSSE({ projectId, invalidate, onEvent, enabled = true }: Options) {
  const qc = useQueryClient()
  const lastIdRef = useRef<number>(0)

  useEffect(() => {
    if (!enabled) return
    if (typeof window !== 'undefined' && (window as { __atlas_disable_sse?: boolean }).__atlas_disable_sse) return
    if (!tokenStore.get()) return

    let es: EventSource | null = null
    let cancelled = false
    let reconnectTimer: number | null = null

    async function connect() {
      // Mint a short-lived single-use stream token via authenticated request.
      // EventSource can't set headers, so the token rides in the URL — but it's
      // burned on first use (server-side) and expires in 30 seconds.
      let streamToken: string
      try {
        const resp = await api<{ token: string; expires_at: string; ttl: number }>(
          '/stream/token', { method: 'POST', body: {} },
        )
        streamToken = resp.token
      } catch {
        // Auth or transport failure — try again on next reconnect cycle.
        if (!cancelled) reconnectTimer = window.setTimeout(connect, 5000)
        return
      }
      if (cancelled) return

      const url = new URL('/api/v1/stream', window.location.origin)
      if (projectId) url.searchParams.set('project_id', String(projectId))
      url.searchParams.set('token', streamToken)
      if (lastIdRef.current > 0) url.searchParams.set('lastEventId', String(lastIdRef.current))

      es = new EventSource(url.toString())

      function handleMessage(ev: MessageEvent) {
        try {
          const data = JSON.parse(ev.data) as SseEvent
          lastIdRef.current = Math.max(lastIdRef.current, data.id)
          if (onEvent) onEvent(data)
          const keys = invalidate?.[data.kind] ?? []
          for (const key of keys) {
            qc.invalidateQueries({ queryKey: key as readonly unknown[] })
          }
        } catch { /* ignore malformed events */ }
      }
      es.addEventListener('message', handleMessage as EventListener)
      if (invalidate) {
        for (const kind of Object.keys(invalidate)) {
          es.addEventListener(kind, handleMessage as EventListener)
        }
      }
      es.onerror = () => {
        // Server cycle ended (8 s) or transport hiccup. Close + mint a fresh
        // stream token, since the previous one is already burned.
        es?.close()
        es = null
        if (!cancelled) reconnectTimer = window.setTimeout(connect, 1000)
      }
    }

    connect()

    return () => {
      cancelled = true
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer)
      es?.close()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, projectId])
}
